import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
    Sparkles, Mail, Zap, Users, Calendar, RefreshCw, TrendingUp,
    ArrowRight, Loader2, LayoutTemplate, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
    webinar_nurture: Calendar,
    cold_outbound: Mail,
    inbound_nurture: Sparkles,
    event_followup: Zap,
    onboarding: Users,
    re_engagement: RefreshCw,
    upsell: TrendingUp,
    custom: LayoutTemplate,
};

const TYPE_COLORS = {
    webinar_nurture: 'from-violet-500 to-purple-600',
    cold_outbound: 'from-blue-500 to-indigo-600',
    inbound_nurture: 'from-amber-500 to-orange-600',
    event_followup: 'from-emerald-500 to-teal-600',
    onboarding: 'from-sky-500 to-cyan-600',
    re_engagement: 'from-rose-500 to-pink-600',
    upsell: 'from-lime-500 to-green-600',
    custom: 'from-stone-400 to-stone-500',
};

function TemplateCard({ template, onSelect }) {
    const Icon = TYPE_ICONS[template.campaign_type] || LayoutTemplate;
    const gradient = TYPE_COLORS[template.campaign_type] || TYPE_COLORS.custom;
    const stepCount = template.steps?.length || 0;
    const hasIf = template.steps?.some(s => s.step_type === 'if_else' || s.step_type === 'branch');
    const hasWaitForEvent = template.steps?.some(s => s.step_type === 'wait_for_event');

    return (
        <div className="group bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-300 hover:shadow-md transition-all cursor-pointer"
            onClick={() => onSelect(template)}>
            {/* Gradient header */}
            <div className={`h-20 bg-gradient-to-br ${gradient} flex items-center justify-center relative`}>
                <Icon className="w-8 h-8 text-white/90" />
                {template.is_system && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-white/20 backdrop-blur rounded text-[9px] text-white font-medium">
                        System
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="p-3">
                <h3 className="text-sm font-semibold text-stone-800 mb-0.5">{template.name}</h3>
                <p className="text-xs text-stone-500 line-clamp-2 mb-2">{template.description}</p>

                <div className="flex items-center gap-2 text-[10px] text-stone-400">
                    <span>{stepCount} steps</span>
                    {hasIf && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">Branching</span>}
                    {hasWaitForEvent && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">Event-driven</span>}
                </div>
            </div>
        </div>
    );
}

export default function CampaignTemplateGallery({ open, onClose }) {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [name, setName] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        axios.get(`${API}/campaigns/templates/gallery`, { headers: getAuthHeader() })
            .then(res => setTemplates(res.data || []))
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, [open, getAuthHeader]);

    const handleSelect = (template) => {
        setSelected(template);
        setName(template.name);
    };

    const handleCreate = async () => {
        if (!name.trim()) { toast.error('Enter a campaign name'); return; }
        setCreating(true);
        try {
            const res = await axios.post(
                `${API}/campaigns/templates/${selected.template_id}/create`,
                { name },
                { headers: getAuthHeader() }
            );
            toast.success('Campaign created from template');
            onClose();
            navigate(`/campaigns/${res.data.campaign_id}/edit`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create');
        } finally {
            setCreating(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LayoutTemplate className="w-5 h-5 text-stone-500" />
                        Campaign Templates
                    </DialogTitle>
                </DialogHeader>

                {selected ? (
                    /* Template selected — name + create */
                    <div className="flex-1 overflow-y-auto space-y-4 py-2">
                        <button onClick={() => setSelected(null)}
                            className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1">
                            ← Back to templates
                        </button>

                        <div className="flex items-start gap-4">
                            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${TYPE_COLORS[selected.campaign_type] || TYPE_COLORS.custom} flex items-center justify-center flex-shrink-0`}>
                                {React.createElement(TYPE_ICONS[selected.campaign_type] || LayoutTemplate, { className: 'w-7 h-7 text-white/90' })}
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-stone-800">{selected.name}</h3>
                                <p className="text-sm text-stone-500 mt-0.5">{selected.description}</p>
                                <p className="text-xs text-stone-400 mt-1">{selected.steps?.length || 0} steps · {selected.edges?.length || 0} connections</p>
                            </div>
                        </div>

                        {/* Step preview */}
                        <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Workflow Steps</p>
                            <div className="space-y-1">
                                {(selected.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((step, i) => (
                                    <div key={step.step_id} className="flex items-center gap-2 text-xs">
                                        <span className="w-5 h-5 rounded-full bg-stone-200 text-stone-600 flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                                            {i + 1}
                                        </span>
                                        <span className="text-stone-600">{step.label}</span>
                                        <span className="text-stone-400 text-[10px]">({step.step_type.replace(/_/g, ' ')})</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Campaign Name</label>
                            <input value={name} onChange={e => setName(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                placeholder="My campaign..." />
                        </div>
                    </div>
                ) : (
                    /* Gallery grid */
                    <div className="flex-1 overflow-y-auto py-2">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                            </div>
                        ) : templates.length === 0 ? (
                            <div className="text-center py-12">
                                <LayoutTemplate className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                                <p className="text-sm text-stone-500">No templates available yet</p>
                                <p className="text-xs text-stone-400 mt-1">Create a campaign and save it as a template</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {templates.map(t => (
                                    <TemplateCard key={t.template_id} template={t} onSelect={handleSelect} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {selected ? (
                        <div className="flex gap-2 w-full justify-end">
                            <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Back</Button>
                            <Button size="sm" onClick={handleCreate} disabled={creating}>
                                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                                Create Campaign
                            </Button>
                        </div>
                    ) : (
                        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
